import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  listProjectOrders,
  setProjectOrdersFilters,
} from '../../modules/projectModule/project-orders';
import ProjectOrdersList from '../../components/project_orders/ProjectOrdersList';
import { ProjectOrdersListBox } from '../../components/project_orders/ProjectOrdersListBox';
import OrderViewModal from '../../components/project_orders/OrderViewModal';

const ProjectOrdersListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { orderLetters, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      orderLetters: projectModule.projectOrders.orderLetters,
      defaultFilters: projectModule.projectOrders.defaultFilters,
      error: projectModule.projectOrders.error,
      loading: loading['project/LIST_PROJECT_ORDERS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

 
  const makeFilters = (filters) => {
    const currentYear = new Date().getFullYear();
    
    // 1. 날짜 기본값 정의 (1월 1일 / 12월 31일)
    const defaultStrt = new Date(currentYear, 0, 1);
    const defaultEnd = new Date(currentYear, 11, 31);

    // 2. 값이 있으면 해당 값 사용, 없으면 기본 날짜 포맷팅
    const searchStrtDt = filters.searchStrtDt 
      ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD') 
      : dayjs(defaultStrt).format('YYYY-MM-DD');
        
    const searchEndDt = filters.searchEndDt 
      ? dayjs(filters.searchEndDt).format('YYYY-MM-DD') 
      : dayjs(defaultEnd).format('YYYY-MM-DD');

    let searchData = {
      [filters.searchKeyword || 'prjNo']: filters.searchWords || '',
      searchStrtDt: searchStrtDt,
      searchEndDt: searchEndDt,
    };

    return searchData;
  };

  useEffect(() => {
    // 컴포넌트 마운트 시 혹은 필터 변경 시 API 호출
    dispatch(listProjectOrders(makeFilters(defaultFilters)));

  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (orderLetters) {
      setTableData(orderLetters);
    }
  }, [orderLetters]);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', hide: true, type: 'string', editable: false },
      { field: 'roUuid', hide: true, type: 'string', editable: false },
      { field: 'expDay', headerName: '계약일', minWidth: 100, flex: 0.5, type: 'string', editable: false },
      { field: 'siDt', headerName: '계약 종료일', minWidth: 100, flex: 0.5, type: 'string', editable: false },
      { field: 'partnerNm', headerName: '매출처명', minWidth: 100, flex: 1, type: 'string', editable: false },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handleOpen(params.row)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),
      },
      { field: 'crtNm', headerName: '등록자', minWidth: 80, flex: 1, type: 'string', editable: false },
      { field: 'crtDt', headerName: '등록일', minWidth: 200, flex: 1, type: 'dateTime', editable: false },
    ],
    [],
  );

  // Formik 초기값 설정 시 Redux 값이 없으면 올해 날짜를 기본으로 사용
  const currentYear = new Date().getFullYear();
  const firstDay = new Date(currentYear, 0, 1);
  const lastDay = new Date(currentYear, 11, 31);

  // 2. initialValues에 기본값 주입
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword || 'prjNo',
    searchWords: defaultFilters.searchWords || '',
    // Redux에 값이 없으면 올해 1월 1일과 12월 31일을 기본값으로 사용
    searchStrtDt: defaultFilters.searchStrtDt || firstDay,
    searchEndDt: defaultFilters.searchEndDt || lastDay,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string().nullable(),
    searchWords: yup.string().trim().nullable(),
    searchStrtDt: yup.date().nullable(),
    searchEndDt: yup
      .date()
      .nullable()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요'),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      // 검색 버튼 클릭 시 필터 정보를 Redux Store에 업데이트
      dispatch(setProjectOrdersFilters(values));
      setSubmitting(false);
    },
  });

  const [showOrder, setShowOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleClose = () => {
    setShowOrder(false);
    setSelectedOrder(null);
  };

  const handleOpen = (param) => {
    setSelectedOrder(param);
    setShowOrder(true);
  };

  return (
    <>
      <BreadcrumbsContainer
        title="수주 관리/수주품의서 현황/수주품의서 리스트"
        icon="order"
      />
      <ProjectOrdersListBox formik={formik} />
      <ProjectOrdersList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
      />
      {showOrder && (
        <OrderViewModal
          id={selectedOrder?.roUuid}
          open={showOrder}
          handleClose={handleClose}
        />
      )}
    </>
  );
};

export default ProjectOrdersListContainer;
